import { useState, useEffect, useMemo } from 'react';
import RazorpayPayment from './RazorpayPayment';
import { FaCheck, FaStar, FaExclamationCircle, FaTag, FaGift, FaMinus, FaPlus, FaArrowLeft, FaArrowRight, FaGlobe, FaMapMarkerAlt, FaBuilding, FaFileAlt, FaClock, FaDollarSign, FaCreditCard } from 'react-icons/fa';
import { toast } from '../../components/ui/Toast';
import { useTheme } from '../../context/SocketContext';
import { useSearchParams } from 'react-router-dom';
import PageHeader from '../../components/PageHeader';
import { FullPageSkeleton } from '../../components/ui/Skeleton';
import {
  usePortalContent,
  getPortalIcon,
  getPricingPageMeta,
  getServiceBadgeMeta,
  getPricingStepHeader,
  getTaxTypeDetails,
  buildInitialTaxAnswers,
  groupTaxQuestionsByForm,
  getActiveTaxTypes,
  formatTemplate,
  buildPricingServices,
  calculateOrderTotalUSD,
  toCheckoutAmounts,
  getPromoDiscount,
  calculateFormationSubtotal,
  getFormationDiscountableAmount,
  getPromoDisplayLabel,
  formatPriceForLocation,
  getSliderExpenseAmount,
  getCompanyFormationConfig,
  getIndianStates,
} from '../../portal';

const Pricing = () => {
  const { currentThemeColors } = useTheme();
  const { content: pricingContent, loading: pricingCmsLoading } = usePortalContent('pricing');
  const pageMeta = useMemo(() => getPricingPageMeta(pricingContent), [pricingContent]);
  const taxTypeDetails = useMemo(() => getTaxTypeDetails(pricingContent), [pricingContent]);
  const formationConfig = useMemo(() => getCompanyFormationConfig(pricingContent), [pricingContent]);
  const companyTypes = formationConfig.companyTypes;
  const usStates = formationConfig.usStates;
  const stateFees = formationConfig.stateFees;
  const indianStates = useMemo(() => getIndianStates(pricingContent), [pricingContent]);
  const L = pageMeta.labels;
  const loc = pageMeta.location;
  const co = pageMeta.checkout;
  const hs = pageMeta.hoursStep;
  const ts = pageMeta.taxStep;
  const PageIcon = getPortalIcon(pageMeta.pageIcon);
  const [searchParams, setSearchParams] = useSearchParams();
  const [selectedService, setSelectedService] = useState(null);
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [location, setLocation] = useState(null);
  const [country, setCountry] = useState('');
  const [step, setStep] = useState(1);
  const [fullName, setFullName] = useState('');
  const [address, setAddress] = useState('');
  const [exchangeRate, setExchangeRate] = useState(85.55);
  const [formErrors, setFormErrors] = useState({});
  const [gstin, setGstin] = useState('');
  const [showGstinModal, setShowGstinModal] = useState(false);
  const [selectedFruit, setSelectedFruit] = useState('');
  const [showTaxBreakup, setShowTaxBreakup] = useState(false);
  const [taxType, setTaxType] = useState('');
  const [taxQuestions, setTaxQuestions] = useState({});
  const [additionalPrice, setAdditionalPrice] = useState(0);
  const [hours, setHours] = useState(1);
  const [companyType, setCompanyType] = useState('');
  const [selectedState, setSelectedState] = useState('');
  const [hasSSN, setHasSSN] = useState(null);
  const [registeredAgent, setRegisteredAgent] = useState(true);
  const [quickPaymentCard, setQuickPaymentCard] = useState(null);
  const [promoCode, setPromoCode] = useState('');
  const [promoCodeDetails, setPromoCodeDetails] = useState(null);
  const [promoCodeError, setPromoCodeError] = useState('');
  const [promoCodeLoading, setPromoCodeLoading] = useState(false);
  const [referralCredits, setReferralCredits] = useState(0);
  const [applyReferralCredit, setApplyReferralCredit] = useState(false);

  // Slider related states
  const [sliderValue, setSliderValue] = useState(1);
  const maxDots = pageMeta.slider?.maxDots || 6;
  const priceIncrement = pageMeta.slider?.priceIncrement || 60;

  const services = useMemo(
    () => buildPricingServices(pricingContent, sliderValue, quickPaymentCard),
    [pricingContent, sliderValue, quickPaymentCard]
  );

  const formationBreakdown = useMemo(() => {
    if (!selectedService || selectedService._id !== 'service_1') return null;
    return calculateFormationSubtotal({
      service: selectedService,
      formationConfig,
      stateFees,
      companyType,
      selectedState,
      hasSSN,
      registeredAgent,
    });
  }, [selectedService, formationConfig, stateFees, companyType, selectedState, hasSSN, registeredAgent]);

  const formationPromoDiscount = useMemo(() => {
    if (!promoCodeDetails || !formationBreakdown) return 0;
    const discountable = getFormationDiscountableAmount(formationBreakdown, promoCodeDetails.applicableFee);
    return getPromoDiscount(promoCodeDetails, discountable);
  }, [promoCodeDetails, formationBreakdown]);

  const formationOrderPreview = useMemo(() => {
    if (!selectedService || selectedService._id !== 'service_1') return null;
    return calculateOrderTotalUSD({
      service: selectedService,
      formationConfig,
      stateFees,
      companyType,
      selectedState,
      hasSSN,
      registeredAgent,
      promoCodeDetails,
      applyReferralCredit,
      referralCredits,
    });
  }, [
    selectedService,
    formationConfig,
    stateFees,
    companyType,
    selectedState,
    hasSSN,
    registeredAgent,
    promoCodeDetails,
    applyReferralCredit,
    referralCredits,
  ]);

  // const fruits = ['Apple', 'Banana', 'Mango', 'Orange', 'Grapes'];

  // Indian states for GST billing
  const fruits = indianStates;

  const allCountries = [
    "United States", "United Kingdom", "Austria", "Afghanistan", "Albania", "Algeria", "American Samoa", "Andorra", "Angola",
    "Anguilla", "Antarctica", "Antigua and Barbuda", "Argentina", "Armenia", "Aruba", "Australia", "Azerbaijan", "Bahamas",
    "Bahrain", "Bangladesh", "Barbados", "Belarus", "Belgium", "Belize", "Benin", "Bermuda", "Bhutan",
    "Bolivia, Plurinational State of", "Bonaire, Sint Eustatius and Saba", "Bosnia and Herzegovina", "Botswana",
    "Bouvet Island", "Brazil", "British Indian Ocean Territory", "Brunei Darussalam", "Bulgaria", "Burkina Faso", "Burundi",
    "Cabo Verde", "Cambodia", "Cameroon", "Canada", "Cayman Islands", "Central African Republic", "Chad", "Chile", "China",
    "Christmas Island", "Cocos (Keeling) Islands", "Colombia", "Comoros", "Congo", "Congo, the Democratic Republic of the",
    "Cook Islands", "Costa Rica", "Croatia", "Cuba", "Curaçao", "Cyprus", "Czechia", "Côte d'Ivoire", "Denmark", "Djibouti",
    "Dominica", "Dominican Republic", "Ecuador", "Egypt", "El Salvador", "Equatorial Guinea", "Eritrea", "Estonia", "Eswatini",
    "Ethiopia", "Falkland Islands (Malvinas)", "Faroe Islands", "Fiji", "Finland", "France", "French Guiana", "French Polynesia",
    "French Southern Territories", "Gabon", "Gambia", "Georgia", "Germany", "Ghana", "Gibraltar", "Greece", "Greenland", "Grenada",
    "Guadeloupe", "Guam", "Guatemala", "Guernsey", "Guinea", "Guinea-Bissau", "Guyana", "Haiti", "Heard Island and McDonald Islands",
    "Holy See", "Honduras", "Hong Kong", "Hungary", "Iceland", "Indonesia", "Iran, Islamic Republic of",
    "Iraq", "Ireland", "Isle of Man", "Israel", "Italy", "Jamaica", "Japan", "Jersey", "Jordan", "Kazakhstan", "Kenya",
    "Kiribati", "Korea, Democratic People's Republic of", "Korea, Republic of", "Kuwait", "Kyrgyzstan",
    "Lao People's Democratic Republic", "Latvia", "Lebanon", "Lesotho", "Liberia", "Libya", "Liechtenstein", "Lithuania",
    "Luxembourg", "Macao", "Madagascar", "Malawi", "Malaysia", "Maldives", "Mali", "Malta", "Marshall Islands", "Martinique",
    "Mauritania", "Mauritius", "Mayotte", "Mexico", "Micronesia, Federated States of", "Moldova, Republic of", "Monaco",
    "Mongolia", "Montenegro", "Montserrat", "Morocco", "Mozambique", "Myanmar", "Namibia", "Nauru", "Nepal", "Netherlands",
    "New Caledonia", "New Zealand", "Nicaragua", "Niger", "Nigeria", "Niue", "Norfolk Island", "North Macedonia",
    "Northern Mariana Islands", "Norway", "Oman", "Pakistan", "Palau", "Palestine, State of", "Panama", "Papua New Guinea",
    "Paraguay", "Peru", "Philippines", "Pitcairn", "Poland", "Portugal", "Puerto Rico", "Qatar", "Réunion", "Romania",
    "Russian Federation", "Rwanda", "Saint Barthélemy", "Saint Helena, Ascension and Tristan da Cunha", "Saint Kitts and Nevis",
    "Saint Lucia", "Saint Martin (French part)", "Saint Pierre and Miquelon", "Saint Vincent and the Grenadines", "Samoa",
    "San Marino", "Sao Tome and Principe", "Saudi Arabia", "Senegal", "Serbia", "Seychelles", "Sierra Leone", "Singapore",
    "Sint Maarten (Dutch part)", "Slovakia", "Slovenia", "Solomon Islands", "Somalia", "South Africa",
    "South Georgia and the South Sandwich Islands", "South Sudan", "Spain", "Sri Lanka", "Sudan", "Suriname",
    "Svalbard and Jan Mayen", "Sweden", "Switzerland", "Syrian Arab Republic", "Taiwan, Province of China", "Tajikistan",
    "Tanzania, United Republic of", "Thailand", "Timor-Leste", "Togo", "Tokelau", "Tonga", "Trinidad and Tobago",
    "Tunisia", "Turkey", "Turkmenistan", "Tuvalu", "Uganda", "Ukraine", "United Arab Emirates", "Uruguay", "Uzbekistan",
    "Vanuatu", "Venezuela, Bolivarian Republic of", "Viet Nam", "Western Sahara", "Yemen", "Zambia", "Zimbabwe"
  ];

  useEffect(() => {
    // Check URL parameters for redirect from unlock payment
    const stepParam = searchParams.get('step');
    const serviceParam = searchParams.get('service');
    
    // Set service if specified
    if (serviceParam) {
      const service = services.find(s => s._id === serviceParam);
      if (service) {
        setSelectedService(service);
      }
    } else if (!selectedService) {
      setSelectedService(services[0]);
    }
    
    // Set step if specified
    if (stepParam) {
      const stepNum = parseFloat(stepParam);
      if (!isNaN(stepNum)) {
        setStep(stepNum);
      }
    }
  }, [searchParams, services, selectedService]);

  useEffect(() => {
    if (!selectedService) return;
    const refreshed = services.find((item) => item._id === selectedService._id);
    if (refreshed && refreshed.price !== selectedService.price) {
      setSelectedService(refreshed);
    }
  }, [services, selectedService]);

  useEffect(() => {
    const fetchExchangeRate = async () => {
      try {
        const response = await fetch('https://api.exchangerate-api.com/v4/latest/USD');
        const data = await response.json();
        setExchangeRate(data.rates.INR);
      } catch (error) {
        console.error('Failed to fetch exchange rate, using default:', error);
      }
    };

    fetchExchangeRate();

    const fetchUser = async () => {
      const token = localStorage.getItem('token');
      if (!token) {
        setLoading(false);
        return;
      }

      try {
        const response = await fetch(`${process.env.REACT_APP_API_BASE_URL}/api/auth/user`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (response.ok) {
          const data = await response.json();
          setUser(data.user);
          
          // Fetch referral credits
          fetchReferralCredits(token);
          
          // Fetch quick payment details after getting user info
          if (data.user && data.user._id) {
            fetchQuickPaymentDetails(data.user._id);
          } else {
            fetchQuickPaymentDetails();
          }
        }
      } catch (error) {
        console.error('Error fetching user:', error);
        // Still try to fetch global payment details if user fetch fails
        fetchQuickPaymentDetails();
      } finally {
        setLoading(false);
      }
    };

    fetchUser();
  }, []);

  const fetchQuickPaymentDetails = async (userId = null) => {
    try {
      // Add userId as query parameter if available
      const endpoint = `${process.env.REACT_APP_API_BASE_URL}/api/payment/quick-payment-details${userId ? `?userId=${userId}` : ''}`;
      const response = await fetch(endpoint);
      
      if (response.ok) {
        const data = await response.json();
        setQuickPaymentCard(data);
      }
    } catch (error) {
      console.error('Error fetching quick payment details:', error);
    }
  };

  const fetchReferralCredits = async (token) => {
    try {
      const response = await fetch(`${process.env.REACT_APP_API_BASE_URL}/api/referrals/credits`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        // referralCredits now stores reward points (1000 points = $5)
        setReferralCredits(data.rewardPoints || data.referralCredits || 0);
        // Auto-apply if points available
        if ((data.rewardPoints || data.referralCredits || 0) > 0) {
          setApplyReferralCredit(true);
        }
      }
    } catch (error) {
      console.error('Error fetching referral credits:', error);
    }
  };

  const activeTaxTypes = useMemo(() => getActiveTaxTypes(taxTypeDetails), [taxTypeDetails]);

  useEffect(() => {
    if (!taxType) {
      setAdditionalPrice(0);
      return;
    }
    let total = 0;
    Object.entries(taxQuestions).forEach(([key, value]) => {
      if (value) {
        const question = taxTypeDetails[taxType].questions.find((q) => q.id === key);
        if (question) {
          total += question.price;
        }
      }
    });
    setAdditionalPrice(total);
  }, [taxQuestions, taxType, taxTypeDetails]);

  useEffect(() => {
    if (taxType && !activeTaxTypes.includes(taxType)) {
      setTaxType('');
      setTaxQuestions({});
    }
  }, [taxType, activeTaxTypes]);

  const handleSliderChange = (event) => {
    const newValue = parseInt(event.target.value, 10);
    setSliderValue(newValue);
  };

  const handleDotClick = (dotIndex) => {
    setSliderValue(dotIndex + 1);
  };

  const handleBuyNow = (e, service) => {
    e.stopPropagation();
    setSelectedService(service);

    if (service._id === 'service_1' && service.hasCompanySelection) {
      setStep(1.1); // Special step for company formation
    } else if (service._id === 'service_2' && service.hasQuestions) {
      setStep(1.5); // Special step for tax service
    } else if (service._id === 'service_3' && service.hasHoursSelection) {
      setStep(1.25); // Special step for Pay-As-You-Go service
    } else if (service._id === 'service_8') {
      // Direct to payment for Annual Report Service
      setStep(2);
    } else {
      setStep(2); // Normal flow for other services
    }
  };

  const handleLocationSelect = (selectedLocation) => {
    setLocation(selectedLocation);
    setCountry('');
    if (selectedLocation === 'india') {
      setSelectedFruit('');
    } else {
      setSelectedFruit('');
      setStep(3);
    }
  };

  const handleProceedWithFruit = () => {
    if (location === 'india' && !selectedFruit) {
      toast.warning(co.selectStateWarning);
      return;
    }
    setStep(3);
  };

  const handleTaxQuestionChange = (questionId, value) => {
    setTaxQuestions(prev => ({
      ...prev,
      [questionId]: value
    }));
  };

  const handleTaxTypeSelect = (type) => {
    setTaxType(type);
    setTaxQuestions(buildInitialTaxAnswers(taxTypeDetails[type]?.questions));
  };

  const handleHoursChange = (e) => {
    const value = parseInt(e.target.value, 10);
    if (value >= 1 && value <= 100) {
      setHours(value);
    }
  };

  const handleCompanyTypeSelect = (type) => {
    setCompanyType(type);
    setSelectedState('');
    setHasSSN(null);
  };

  const handleStateSelect = (state) => {
    setSelectedState(state);
  };

  const handleSSNSelect = (hasSSN) => {
    setHasSSN(hasSSN);
  };

  const handleRegisteredAgentChange = (e) => {
    setRegisteredAgent(e.target.checked);
  };

  const validatePromoCode = async () => {
    if (!promoCode.trim()) {
      setPromoCodeError('Please enter a promo code');
      return;
    }

    if (!selectedService) {
      setPromoCodeError('Please select a service first');
      return;
    }

    setPromoCodeLoading(true);
    setPromoCodeError('');

    try {
      const response = await fetch(`${process.env.REACT_APP_API_BASE_URL}/api/promo-codes/validate`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          code: promoCode.trim(),
          serviceName: selectedService.name,
          userId: user ? user._id : null
        })
      });

      const data = await response.json();

      if (data.success) {
        setPromoCodeDetails(data.promoCode);
        setPromoCodeError('');
      } else {
        setPromoCodeDetails(null);
        setPromoCodeError(data.message || 'Invalid promo code');
      }
    } catch (error) {
      console.error('Error validating promo code:', error);
      setPromoCodeError('Failed to validate promo code. Please try again.');
      setPromoCodeDetails(null);
    } finally {
      setPromoCodeLoading(false);
    }
  };

  const clearPromoCode = () => {
    setPromoCode('');
    setPromoCodeDetails(null);
    setPromoCodeError('');
  };

  const getStepClass = (stepNumber) => {
    if (stepNumber === 1.1) {
      return step >= 1.1 ? 'active-step' : '';
    }
    if (stepNumber === 1.25) {
      return step >= 1.25 ? 'active-step' : '';
    }
    if (stepNumber === 1.5) {
      return step >= 2 ? 'active-step' : (step === 1.5 ? 'active-step' : '');
    }
    return step >= stepNumber ? 'active-step' : '';
  };

  const getConvertedPrice = () => {
    if (!selectedService) return { basePrice: 0, gst: 0, total: 0, discount: 0, referralDiscount: 0 };

    const order = calculateOrderTotalUSD({
      service: selectedService,
      formationConfig,
      stateFees,
      companyType,
      selectedState,
      hasSSN,
      registeredAgent,
      additionalPrice: selectedService._id === 'service_2' ? additionalPrice : 0,
      hours: selectedService._id === 'service_3' ? hours : 1,
      promoCodeDetails,
      applyReferralCredit,
      referralCredits,
    });

    const amounts = toCheckoutAmounts(order.totalUsd, { location, exchangeRate });

    if (location === 'india') {
      return {
        ...amounts,
        discount: (order.discount * exchangeRate).toFixed(2),
        referralDiscount: (order.referralDiscount * exchangeRate).toFixed(2),
      };
    }

    return {
      ...amounts,
      discount: order.discount.toFixed(2),
      referralDiscount: order.referralDiscount.toFixed(2),
    };
  };

  const validateGSTIN = (gstin) => {
    if (!gstin) return true;
    const regex = /^[0-9]{2}[A-Z]{5}[0-9]{4}[A-Z]{1}[1-9A-Z]{1}Z[0-9A-Z]{1}$/;
    return regex.test(gstin);
  };

  const isServicePurchased = (serviceId) => {
    if (!user) return false;

    switch (serviceId) {
      case 'service_1':
        return user.isUSAEnabled;
      case 'service_2':
        return user.isTaxFilingEnabled;
      case 'service_4':
        return user.isBookkeepingEnabled;
      case 'service_5':
        return user.isCFOEnabled;
      case 'service_6':
        return user.isQuickEnabled;
      case 'service_7':
        return user.isRegisteredAgentEnabled;
      case 'service_8':
        return user.isAnnualReportServiceEnabled;
      default:
        return false;
    }
  };

  const stepHeader = getPricingStepHeader(pageMeta, step);
  const pricingTitle = stepHeader.title;
  const pricingSubtitle = stepHeader.subtitle;

  const pricingStepTracker = (
    <div className="flex items-center gap-2 md:gap-4 flex-wrap">
      <div className={`flex items-center gap-2 px-3 py-2 rounded-full text-sm font-medium transition-all ${step >= 1 ? 'bg-primary-500 text-white' : 'bg-gray-100 dark:bg-dark-700 text-gray-500 dark:text-gray-400'}`}>
        <span className="w-6 h-6 rounded-full bg-white/20 flex items-center justify-center text-xs">1</span>
        <span className="hidden sm:inline">{pageMeta.stepTracker.selectPlan}</span>
      </div>
      {selectedService?._id === 'service_1' && (
        <div className={`flex items-center gap-2 px-3 py-2 rounded-full text-sm font-medium transition-all ${step >= 1.1 ? 'bg-primary-500 text-white' : 'bg-gray-100 dark:bg-dark-700 text-gray-500 dark:text-gray-400'}`}>
          <span className="w-6 h-6 rounded-full bg-white/20 flex items-center justify-center text-xs">1.1</span>
          <span className="hidden sm:inline">{pageMeta.stepTracker.company}</span>
        </div>
      )}
      {selectedService?._id === 'service_3' && (
        <div className={`flex items-center gap-2 px-3 py-2 rounded-full text-sm font-medium transition-all ${step >= 1.25 ? 'bg-primary-500 text-white' : 'bg-gray-100 dark:bg-dark-700 text-gray-500 dark:text-gray-400'}`}>
          <span className="w-6 h-6 rounded-full bg-white/20 flex items-center justify-center text-xs">1.2</span>
          <span className="hidden sm:inline">{pageMeta.stepTracker.hours}</span>
        </div>
      )}
      {selectedService?._id === 'service_2' && (
        <div className={`flex items-center gap-2 px-3 py-2 rounded-full text-sm font-medium transition-all ${step >= 1.5 ? 'bg-primary-500 text-white' : 'bg-gray-100 dark:bg-dark-700 text-gray-500 dark:text-gray-400'}`}>
          <span className="w-6 h-6 rounded-full bg-white/20 flex items-center justify-center text-xs">1.5</span>
          <span className="hidden sm:inline">{pageMeta.stepTracker.taxDetails}</span>
        </div>
      )}
      <div className={`flex items-center gap-2 px-3 py-2 rounded-full text-sm font-medium transition-all ${step >= 2 ? 'bg-primary-500 text-white' : 'bg-gray-100 dark:bg-dark-700 text-gray-500 dark:text-gray-400'}`}>
        <span className="w-6 h-6 rounded-full bg-white/20 flex items-center justify-center text-xs">2</span>
        <span className="hidden sm:inline">{pageMeta.stepTracker.location}</span>
      </div>
      <div className={`flex items-center gap-2 px-3 py-2 rounded-full text-sm font-medium transition-all ${step >= 3 ? 'bg-primary-500 text-white' : 'bg-gray-100 dark:bg-dark-700 text-gray-500 dark:text-gray-400'}`}>
        <span className="w-6 h-6 rounded-full bg-white/20 flex items-center justify-center text-xs">3</span>
        <span className="hidden sm:inline">{pageMeta.stepTracker.checkout}</span>
      </div>
    </div>
  );

  if (loading || pricingCmsLoading) {
    return (
      <div className="space-y-6">
        <FullPageSkeleton message={pageMeta.loadingMessage} />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* GSTIN Modal */}
      {showGstinModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm p-4">
          <div className="bg-white dark:bg-dark-800 rounded-2xl shadow-2xl w-full max-w-md animate-scale-in">
            <div className="p-6 border-b border-gray-100 dark:border-dark-700">
              <h3 className="text-lg font-bold text-dark-800 dark:text-white">{co.gstinModalTitle}</h3>
              <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">{co.gstinModalSubtitle}</p>
            </div>
            <div className="p-6">
              <input
                type="text"
                placeholder={co.gstinPlaceholder}
                value={gstin}
                onChange={(e) => setGstin(e.target.value.toUpperCase())}
                maxLength="15"
                className="w-full p-4 rounded-xl bg-gray-50 dark:bg-dark-700 border border-gray-200 dark:border-dark-600 text-dark-800 dark:text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-primary-500/50 focus:border-primary-500 transition-all duration-200 font-mono tracking-wider"
              />
              {gstin && !validateGSTIN(gstin) && (
                <p className="mt-2 text-sm text-red-500">{co.gstinInvalidMessage}</p>
              )}
            </div>
            <div className="p-6 border-t border-gray-100 dark:border-dark-700 flex gap-3">
              <button 
                onClick={() => setShowGstinModal(false)}
                className="flex-1 px-4 py-2.5 rounded-xl font-medium text-gray-600 dark:text-gray-400 bg-gray-100 dark:bg-dark-700 hover:bg-gray-200 dark:hover:bg-dark-600 transition-colors"
              >
                {L.cancel}
              </button>
              <button
                onClick={() => {
                  if (!gstin || validateGSTIN(gstin)) {
                    setShowGstinModal(false);
                  }
                }}
                className="flex-1 px-4 py-2.5 rounded-xl font-medium text-white bg-gradient-to-r from-primary-500 to-primary-600 hover:from-primary-600 hover:to-primary-700 transition-all duration-200"
              >
                {L.save}
              </button>
            </div>
          </div>
        </div>
      )}

      <PageHeader
        icon={PageIcon}
        title={pricingTitle}
        subtitle={pricingSubtitle || undefined}
        action={pricingStepTracker}
      />

      {step === 1 && (
        <>
          {/* First Row - Fixed Pricing Cards (3 services) */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 opacity-0 animate-fade-in-up" style={{ animationDelay: '100ms', animationFillMode: 'forwards' }}>
            {services.slice(0, 3).map((service) => {
              const isPurchased = isServicePurchased(service._id);
              const badge = getServiceBadgeMeta(service, pageMeta);

              return (
                <div
                  key={service._id}
                  className={`card relative overflow-hidden cursor-pointer transition-all duration-300 hover:shadow-xl flex flex-col ${
                    selectedService?._id === service._id ? 'ring-2 ring-primary-500 shadow-lg shadow-primary-500/20' : ''
                  } ${badge.visible ? 'border-2 border-primary-500' : ''} ${isPurchased ? 'opacity-60 cursor-not-allowed' : ''}`}
                  onClick={() => !isPurchased && setSelectedService(service)}
                >
                  {badge.visible && (
                    <div className={`absolute top-0 right-0 text-white text-xs font-semibold px-4 py-1.5 rounded-bl-xl flex items-center gap-1 ${badge.styleClass}`}>
                      <FaStar className="w-3 h-3" /> {badge.label}
                    </div>
                  )}

                  <div className="p-6 flex flex-col flex-1">
                    <h3 className="text-lg font-bold text-dark-800 dark:text-white mb-4">{service.name}</h3>
                    <div className="mb-6">
                      <span className="text-4xl font-bold text-primary-600 dark:text-primary-400">${service.price}</span>
                      <span className="text-gray-500 dark:text-gray-400 text-sm">/{service.frequency}</span>
                    </div>
                    <div className="space-y-3 flex-1">
                      {service.details.map((detail, index) => (
                        <p key={index} className="flex items-start gap-2 text-sm text-gray-600 dark:text-gray-400">
                          <FaCheck className="w-4 h-4 text-green-500 flex-shrink-0 mt-0.5" /> {detail}
                        </p>
                      ))}
                    </div>
                    <button
                      className={`w-full py-3 rounded-xl font-semibold transition-all duration-200 mt-6 ${
                        !user || (isPurchased && service._id !== 'service_7') 
                          ? 'bg-gray-200 dark:bg-dark-600 text-gray-500 dark:text-gray-400 cursor-not-allowed' 
                          : 'bg-gradient-to-r from-primary-500 to-primary-600 text-white hover:shadow-lg hover:shadow-primary-500/30'
                      }`}
                      onClick={(e) => handleBuyNow(e, service)}
                      disabled={!user || (isPurchased && service._id !== 'service_7')}
                    >
                      {!user ? pageMeta.loginToPurchaseLabel : (isPurchased && service._id !== 'service_7') ? pageMeta.alreadyPurchasedLabel : pageMeta.buyNowLabel}
                    </button>
                  </div>
                </div>
              );
            })}
          </div>

          {/* Enhanced Slider Section */}
          <div className="card p-6">
            <div className="max-w-2xl mx-auto">
              {/* Custom Slider Track */}
              <div className="relative mb-6 py-4">
                {/* Track Background */}
                <div className="absolute top-1/2 left-0 right-0 h-1 bg-gray-200 dark:bg-dark-600 rounded-full -translate-y-1/2"></div>
                
                {/* Track Progress */}
                <div 
                  className="absolute top-1/2 left-0 h-1 bg-gradient-to-r from-primary-400 to-primary-500 rounded-full -translate-y-1/2 transition-all duration-300 ease-out"
                  style={{ width: `${((sliderValue - 1) / (maxDots - 1)) * 100}%` }}
                ></div>
                
                {/* Slider Thumb */}
                <div 
                  className="absolute top-1/2 -translate-y-1/2 -translate-x-1/2 transition-all duration-300 ease-out"
                  style={{ left: `${((sliderValue - 1) / (maxDots - 1)) * 100}%` }}
                >
                  <div className="w-5 h-5 bg-primary-500 rounded-full shadow-lg shadow-primary-500/40 ring-4 ring-white dark:ring-dark-800 cursor-pointer hover:scale-110 transition-transform"></div>
                </div>
                
                {/* Hidden Range Input */}
                <input
                  type="range"
                  className="absolute inset-0 w-full h-full opacity-0 cursor-pointer z-10"
                  min="1"
                  max={maxDots}
                  step="1"
                  value={sliderValue}
                  onChange={handleSliderChange}
                />
              </div>
              
              {/* Monthly Expense Text */}
              <p className="text-center text-sm font-medium text-gray-600 dark:text-gray-400">
                {pageMeta.slider.labelPrefix}{' '}
                <span className="text-primary-500 font-bold text-base transition-all duration-300">
                  ${getSliderExpenseAmount(pageMeta, sliderValue).toLocaleString()}
                </span>{' '}
                {pageMeta.slider.labelSuffix}
              </p>
            </div>
          </div>

          {/* Second Row - Dynamic Pricing Cards (remaining services) */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {services.slice(3).map((service) => {
              const isPurchased = isServicePurchased(service._id);
              const badge = getServiceBadgeMeta(service, pageMeta);

              return (
                <div
                  key={service._id}
                  className={`card relative overflow-hidden cursor-pointer transition-all duration-300 hover:shadow-xl flex flex-col ${
                    selectedService?._id === service._id ? 'ring-2 ring-primary-500 shadow-lg shadow-primary-500/20' : ''
                  } ${badge.visible ? 'border-2 border-primary-500' : ''} ${isPurchased ? 'opacity-60 cursor-not-allowed' : ''}`}
                  onClick={() => !isPurchased && setSelectedService(service)}
                >
                  {badge.visible && (
                    <div className={`absolute top-0 right-0 text-white text-xs font-semibold px-4 py-1.5 rounded-bl-xl flex items-center gap-1 ${badge.styleClass}`}>
                      <FaStar className="w-3 h-3" /> {badge.label}
                    </div>
                  )}

                  <div className="p-6 flex flex-col flex-1">
                    <h3 className="text-lg font-bold text-dark-800 dark:text-white mb-4">{service.name}</h3>
                    <div className="mb-6">
                      <span className="text-4xl font-bold text-primary-600 dark:text-primary-400">${service.price}</span>
                      <span className="text-gray-500 dark:text-gray-400 text-sm">/{service.frequency}</span>
                    </div>
                    <div className="space-y-3 flex-1">
                      {service.details.map((detail, index) => (
                        <p key={index} className="flex items-start gap-2 text-sm text-gray-600 dark:text-gray-400">
                          <FaCheck className="w-4 h-4 text-green-500 flex-shrink-0 mt-0.5" /> {detail}
                        </p>
                      ))}
                    </div>
                    <button
                      className={`w-full py-3 rounded-xl font-semibold transition-all duration-200 mt-6 ${
                        !user || (isPurchased && service._id !== 'service_7') 
                          ? 'bg-gray-200 dark:bg-dark-600 text-gray-500 dark:text-gray-400 cursor-not-allowed' 
                          : 'bg-gradient-to-r from-primary-500 to-primary-600 text-white hover:shadow-lg hover:shadow-primary-500/30'
                      }`}
                      onClick={(e) => handleBuyNow(e, service)}
                      disabled={!user || (isPurchased && service._id !== 'service_7')}
                    >
                      {!user ? pageMeta.loginToPurchaseLabel : (isPurchased && service._id !== 'service_7') ? pageMeta.alreadyPurchasedLabel : pageMeta.buyNowLabel}
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        </>
      )}

      {step === 1.1 && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Left Column - Selection Options */}
          <div className="card p-6 space-y-6">
            {/* Company Type Selection */}
            <div>
              <h3 className="text-lg font-semibold text-dark-800 dark:text-white mb-4">{formationConfig.companyTypeTitle}</h3>
              <div className="grid grid-cols-2 gap-3">
                {companyTypes.map((type) => (
                  <div
                    key={type}
                    className={`p-4 rounded-xl border-2 cursor-pointer transition-all duration-200 ${
                      companyType === type 
                        ? 'border-primary-500 bg-primary-50 dark:bg-primary-900/20' 
                        : 'border-gray-200 dark:border-dark-600 hover:border-primary-300 dark:hover:border-primary-700'
                    }`}
                    onClick={() => handleCompanyTypeSelect(type)}
                  >
                    <div className="flex items-center gap-3">
                      <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center ${
                        companyType === type ? 'border-primary-500' : 'border-gray-300 dark:border-dark-500'
                      }`}>
                        {companyType === type && <div className="w-2.5 h-2.5 rounded-full bg-primary-500" />}
                      </div>
                      <span className="font-medium text-dark-800 dark:text-white">{type}</span>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* State Selection */}
            {companyType && (
              <div>
                <h3 className="text-lg font-semibold text-dark-800 dark:text-white mb-4">{formationConfig.stateTitle}</h3>
                <div className="grid grid-cols-2 gap-3">
                  {usStates.map((state) => (
                    <div
                      key={state}
                      className={`p-4 rounded-xl border-2 cursor-pointer transition-all duration-200 ${
                        selectedState === state 
                          ? 'border-primary-500 bg-primary-50 dark:bg-primary-900/20' 
                          : 'border-gray-200 dark:border-dark-600 hover:border-primary-300 dark:hover:border-primary-700'
                      }`}
                      onClick={() => handleStateSelect(state)}
                    >
                      <div className="flex items-center gap-3">
                        <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center ${
                          selectedState === state ? 'border-primary-500' : 'border-gray-300 dark:border-dark-500'
                        }`}>
                          {selectedState === state && <div className="w-2.5 h-2.5 rounded-full bg-primary-500" />}
                        </div>
                        <span className="font-medium text-dark-800 dark:text-white">{state}</span>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* EIN Number */}
            {selectedState && (
              <div>
                <h3 className="text-lg font-semibold text-dark-800 dark:text-white mb-2">{formationConfig.einTitle}</h3>
                <p className="text-sm text-gray-500 dark:text-gray-400 mb-4">{formationConfig.ssnQuestion}</p>
                <div className="grid grid-cols-2 gap-3">
                  <button
                    className={`p-4 rounded-xl border-2 font-medium transition-all duration-200 ${
                      hasSSN === true 
                        ? 'border-primary-500 bg-primary-50 dark:bg-primary-900/20 text-primary-600 dark:text-primary-400' 
                        : 'border-gray-200 dark:border-dark-600 text-dark-800 dark:text-white hover:border-primary-300'
                    }`}
                    onClick={() => handleSSNSelect(true)}
                  >
                    {L.yes} (${formationConfig.einYesPrice})
                  </button>
                  <button
                    className={`p-4 rounded-xl border-2 font-medium transition-all duration-200 ${
                      hasSSN === false 
                        ? 'border-primary-500 bg-primary-50 dark:bg-primary-900/20 text-primary-600 dark:text-primary-400' 
                        : 'border-gray-200 dark:border-dark-600 text-dark-800 dark:text-white hover:border-primary-300'
                    }`}
                    onClick={() => handleSSNSelect(false)}
                  >
                    {L.no} (${formationConfig.einNoPrice})
                  </button>
                </div>
              </div>
            )}

            {/* Registered Agent */}
            {hasSSN !== null && (
              <div className="p-4 rounded-xl bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800/30">
                <div className="flex items-center gap-2">
                  <FaCheck className="w-4 h-4 text-green-500" />
                  <span className="text-green-700 dark:text-green-400 font-medium">{formationConfig.registeredAgentIncluded}</span>
                </div>
              </div>
            )}
          </div>

          {/* Right Column - Fees Breakdown */}
          {selectedState && (
            <div className="card p-6 space-y-6">
              {/* Promo Code Section */}
              <div>
                <h3 className="text-lg font-semibold text-dark-800 dark:text-white mb-4 flex items-center gap-2">
                  <FaTag className="w-4 h-4 text-primary-500" /> {formationConfig.promoCodeTitle}
                </h3>
                <div className="flex gap-2">
                  <input
                    type="text"
                    placeholder={formationConfig.promoCodePlaceholder}
                    value={promoCode}
                    onChange={(e) => setPromoCode(e.target.value.toUpperCase())}
                    disabled={promoCodeLoading}
                    className="flex-1 p-3 rounded-xl bg-gray-50 dark:bg-dark-700 border border-gray-200 dark:border-dark-600 text-dark-800 dark:text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-primary-500/50 focus:border-primary-500 transition-all duration-200 font-mono"
                  />
                  <button
                    onClick={validatePromoCode}
                    disabled={promoCodeLoading || !promoCode.trim()}
                    className="px-6 py-3 rounded-xl font-medium text-white bg-primary-500 hover:bg-primary-600 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                  >
                    {promoCodeLoading ? '...' : L.apply}
                  </button>
                </div>
                {promoCodeError && (
                  <p className="mt-2 text-sm text-red-500">{promoCodeError}</p>
                )}
                {promoCodeDetails && (
                  <div className="mt-3 p-3 rounded-xl bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800/30 flex items-center justify-between">
                    <span className="text-green-700 dark:text-green-400 text-sm">
                      ✓ {getPromoDisplayLabel(promoCodeDetails)} applied to {promoCodeDetails.applicableFee}
                    </span>
                    <button onClick={clearPromoCode} className="text-red-500 text-sm font-medium hover:text-red-600">
                      {L.remove}
                    </button>
                  </div>
                )}
              </div>

              {/* Referral Credits Section */}
              {referralCredits > 0 && (
                <div>
                  <h3 className="text-lg font-semibold text-dark-800 dark:text-white mb-4 flex items-center gap-2">
                    <FaGift className="w-4 h-4 text-primary-500" /> {formationConfig.rewardPointsTitle}
                  </h3>
                  <label className="flex items-center gap-3 p-4 rounded-xl bg-gray-50 dark:bg-dark-700 border border-gray-200 dark:border-dark-600 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={applyReferralCredit}
                      onChange={(e) => setApplyReferralCredit(e.target.checked)}
                      className="w-5 h-5 rounded border-gray-300 text-primary-500 focus:ring-primary-500"
                    />
                    <span className="text-dark-800 dark:text-white">{formatTemplate(formationConfig.rewardPointsApply, { points: referralCredits })}</span>
                  </label>
                  {applyReferralCredit && (
                    <p className="mt-2 text-sm text-green-600 dark:text-green-400">{formatTemplate(formationConfig.rewardPointsApplied, { points: referralCredits })}</p>
                  )}
                </div>
              )}

              {/* Fees Breakdown */}
              {formationBreakdown && (
              <div>
                <h3 className="text-lg font-semibold text-dark-800 dark:text-white mb-4">{formationConfig.feesBreakdownTitle}</h3>
                <div className="space-y-3">
                  <div className="flex justify-between py-2 border-b border-gray-100 dark:border-dark-700">
                    <span className="text-gray-600 dark:text-gray-400">{formationConfig.ourFeeLabel}</span>
                    <span className="font-medium text-dark-800 dark:text-white">
                      {promoCodeDetails && promoCodeDetails.applicableFee === 'Our Fee' ? (
                        <>
                          <span className="line-through text-gray-400 mr-2">${formationBreakdown.baseFee}</span>
                          <span className="text-green-500">${(formationBreakdown.baseFee - formationPromoDiscount).toFixed(2)}</span>
                        </>
                      ) : `$${formationBreakdown.baseFee}`}
                    </span>
                  </div>
                  {formationPromoDiscount > 0 && (
                    <div className="flex justify-between py-2 border-b border-gray-100 dark:border-dark-700">
                      <span className="text-gray-600 dark:text-gray-400">{formationConfig.promoDiscountLabel}</span>
                      <span className="font-medium text-green-500">-${formationPromoDiscount.toFixed(2)}</span>
                    </div>
                  )}
                  <div className="flex justify-between py-2 border-b border-gray-100 dark:border-dark-700">
                    <span className="text-gray-600 dark:text-gray-400">{formationConfig.stateFeeLabel}</span>
                    <span className="font-medium text-dark-800 dark:text-white">${formationBreakdown.stateFee}</span>
                  </div>
                  {hasSSN !== null && (
                    <div className="flex justify-between py-2 border-b border-gray-100 dark:border-dark-700">
                      <span className="text-gray-600 dark:text-gray-400">{formationConfig.einServiceLabel}</span>
                      <span className="font-medium text-dark-800 dark:text-white">${formationBreakdown.einFee}</span>
                    </div>
                  )}
                  {!registeredAgent && (
                    <div className="flex justify-between py-2 border-b border-gray-100 dark:border-dark-700">
                      <span className="text-gray-600 dark:text-gray-400">{formationConfig.registeredAgentLabel}</span>
                      <span className="font-medium text-dark-800 dark:text-white">${formationBreakdown.raFee}</span>
                    </div>
                  )}
                  <div className="flex justify-between py-3 bg-primary-50 dark:bg-primary-900/20 rounded-xl px-4 -mx-4">
                    <span className="font-semibold text-dark-800 dark:text-white">{L.total}</span>
                    <span className="font-bold text-xl text-primary-600 dark:text-primary-400">
                      ${formationOrderPreview?.totalUsd.toFixed(2)}
                    </span>
                  </div>
                </div>
              </div>
              )}

              {/* Action Buttons */}
              <div className="flex gap-3 pt-4">
                <button
                  onClick={() => setStep(1)}
                  className="flex-1 flex items-center justify-center gap-2 px-6 py-3 rounded-xl font-medium text-gray-600 dark:text-gray-400 bg-gray-100 dark:bg-dark-700 hover:bg-gray-200 dark:hover:bg-dark-600 transition-colors"
                >
                  <FaArrowLeft className="w-4 h-4" /> {L.back}
                </button>
                <button
                  onClick={() => setStep(2)}
                  disabled={!companyType || !selectedState || hasSSN === null}
                  className="flex-1 flex items-center justify-center gap-2 px-6 py-3 rounded-xl font-medium text-white bg-gradient-to-r from-primary-500 to-primary-600 hover:from-primary-600 hover:to-primary-700 disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-200"
                >
                  {L.continue} <FaArrowRight className="w-4 h-4" />
                </button>
              </div>
            </div>
          )}
        </div>
      )}

      {step === 1.25 && (
        <div className="max-w-lg mx-auto">
          <div className="card p-6 space-y-6">
            {/* Hours Input */}
            <div>
              <label className="block text-lg font-semibold text-dark-800 dark:text-white mb-4 text-center">
                {hs.title}
              </label>
              <div className="flex items-center justify-center gap-4">
                <button
                  onClick={() => setHours(Math.max(1, hours - 1))}
                  className="w-12 h-12 rounded-xl bg-gray-100 dark:bg-dark-700 text-dark-800 dark:text-white hover:bg-gray-200 dark:hover:bg-dark-600 transition-colors flex items-center justify-center"
                >
                  <FaMinus className="w-4 h-4" />
                </button>
                <input
                  type="number"
                  min="1"
                  max="100"
                  value={hours}
                  onChange={handleHoursChange}
                  className="w-24 h-12 text-center text-2xl font-bold rounded-xl bg-gray-50 dark:bg-dark-700 border border-gray-200 dark:border-dark-600 text-dark-800 dark:text-white focus:outline-none focus:ring-2 focus:ring-primary-500/50"
                />
                <button
                  onClick={() => setHours(Math.min(100, hours + 1))}
                  className="w-12 h-12 rounded-xl bg-gray-100 dark:bg-dark-700 text-dark-800 dark:text-white hover:bg-gray-200 dark:hover:bg-dark-600 transition-colors flex items-center justify-center"
                >
                  <FaPlus className="w-4 h-4" />
                </button>
              </div>
            </div>

            {/* Summary */}
            <div className="p-4 rounded-xl bg-gray-50 dark:bg-dark-700 space-y-3">
              <div className="flex justify-between">
                <span className="text-gray-600 dark:text-gray-400">{hs.hourlyRateLabel}</span>
                <span className="font-medium text-dark-800 dark:text-white">${selectedService.price}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-600 dark:text-gray-400">{hs.hoursSelectedLabel}</span>
                <span className="font-medium text-dark-800 dark:text-white">{hours}</span>
              </div>
              <div className="border-t border-gray-200 dark:border-dark-600 pt-3">
                <div className="flex justify-between">
                  <span className="font-semibold text-dark-800 dark:text-white">{hs.totalAmountLabel}</span>
                  <span className="font-bold text-xl text-primary-600 dark:text-primary-400">${(selectedService.price * hours).toFixed(2)}</span>
                </div>
              </div>
            </div>

            {/* Action Buttons */}
            <div className="flex gap-3">
              <button
                onClick={() => setStep(1)}
                className="flex-1 flex items-center justify-center gap-2 px-6 py-3 rounded-xl font-medium text-gray-600 dark:text-gray-400 bg-gray-100 dark:bg-dark-700 hover:bg-gray-200 dark:hover:bg-dark-600 transition-colors"
              >
                <FaArrowLeft className="w-4 h-4" /> {L.back}
              </button>
              <button
                onClick={() => setStep(2)}
                className="flex-1 flex items-center justify-center gap-2 px-6 py-3 rounded-xl font-medium text-white bg-gradient-to-r from-primary-500 to-primary-600 hover:from-primary-600 hover:to-primary-700 transition-all duration-200"
              >
                {L.continue} <FaArrowRight className="w-4 h-4" />
              </button>
            </div>
          </div>
        </div>
      )}

      {step === 1.5 && (
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Left Column - Tax Type & Questions */}
          <div className="lg:col-span-2 card p-6 space-y-6">
            {/* Tax Type Selection */}
            <div>
              <h3 className="text-lg font-semibold text-dark-800 dark:text-white mb-4">{ts.selectTaxTypeTitle}</h3>
              <div className="space-y-3">
                {activeTaxTypes.map((type) => (
                  <div
                    key={type}
                    className={`p-4 rounded-xl border-2 cursor-pointer transition-all duration-200 ${
                      taxType === type
                        ? 'border-primary-500 bg-primary-50 dark:bg-primary-900/20'
                        : 'border-gray-200 dark:border-dark-600 hover:border-primary-300'
                    }`}
                    onClick={() => handleTaxTypeSelect(type)}
                  >
                    <div className="flex items-start gap-3">
                      <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center mt-0.5 ${
                        taxType === type ? 'border-primary-500' : 'border-gray-300 dark:border-dark-500'
                      }`}>
                        {taxType === type && <div className="w-2.5 h-2.5 rounded-full bg-primary-500" />}
                      </div>
                      <div>
                        <span className="font-medium text-dark-800 dark:text-white block">{taxTypeDetails[type].title}</span>
                        {taxTypeDetails[type].subtitle && (
                          <span className="text-sm text-gray-500 dark:text-gray-400 mt-1 block">{taxTypeDetails[type].subtitle}</span>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Form-wise Questions */}
            {taxType && (
              <div className="space-y-6">
                <h3 className="text-lg font-semibold text-dark-800 dark:text-white">{ts.formQuestionsTitle || ts.additionalQuestionsTitle}</h3>
                {groupTaxQuestionsByForm(taxTypeDetails[taxType]).map((formGroup) => (
                  <div key={formGroup.id} className="space-y-3">
                    <div className="rounded-xl bg-primary-50 dark:bg-primary-900/20 border border-primary-100 dark:border-primary-800/30 px-4 py-3">
                      <h4 className="font-semibold text-dark-800 dark:text-white">{formGroup.name}</h4>
                      {formGroup.description && (
                        <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">{formGroup.description}</p>
                      )}
                    </div>
                    {formGroup.questions.map((question) => (
                      <div key={question.id} className="p-4 rounded-xl bg-gray-50 dark:bg-dark-700 border border-gray-200 dark:border-dark-600">
                        <p className="text-sm text-dark-800 dark:text-white mb-3">{question.text}</p>
                        <div className="flex gap-2">
                          <button
                            className={`px-6 py-2 rounded-lg text-sm font-medium transition-all ${
                              taxQuestions[question.id] === true
                                ? 'bg-green-500 text-white'
                                : 'bg-gray-200 dark:bg-dark-600 text-gray-600 dark:text-gray-400 hover:bg-gray-300'
                            }`}
                            onClick={() => handleTaxQuestionChange(question.id, true)}
                          >
                            {L.yes} {formatTemplate(ts.yesPriceSuffix || '(+${price})', { price: question.price })}
                          </button>
                          <button
                            className={`px-6 py-2 rounded-lg text-sm font-medium transition-all ${
                              taxQuestions[question.id] === false
                                ? 'bg-gray-500 text-white'
                                : 'bg-gray-200 dark:bg-dark-600 text-gray-600 dark:text-gray-400 hover:bg-gray-300'
                            }`}
                            onClick={() => handleTaxQuestionChange(question.id, false)}
                          >
                            {L.no}
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Right Column - Price Summary */}
          <div className="card p-6 h-fit sticky top-6">
            <h3 className="text-lg font-semibold text-dark-800 dark:text-white mb-4">{ts.priceSummaryTitle}</h3>
            <div className="space-y-3">
              <div className="flex justify-between py-2 border-b border-gray-100 dark:border-dark-700">
                <span className="text-gray-600 dark:text-gray-400">{ts.basePriceLabel}</span>
                <span className="font-medium text-dark-800 dark:text-white">${selectedService.price}</span>
              </div>
              {taxType && (
                <div className="py-2 border-b border-gray-100 dark:border-dark-700">
                  <span className="text-gray-600 dark:text-gray-400 block mb-2">{ts.addOnsLabel || 'Selected add-ons:'}</span>
                  {taxTypeDetails[taxType].questions.filter((question) => taxQuestions[question.id]).length === 0 ? (
                    <span className="text-sm text-gray-400">None selected</span>
                  ) : (
                    taxTypeDetails[taxType].questions
                      .filter((question) => taxQuestions[question.id])
                      .map((question) => (
                        <div key={question.id} className="flex justify-between py-1 gap-3">
                          <span className="text-gray-600 dark:text-gray-400 text-sm">{question.text}</span>
                          <span className="font-medium text-green-500 whitespace-nowrap">+${question.price}</span>
                        </div>
                      ))
                  )}
                </div>
              )}
              <div className="flex justify-between py-3 bg-primary-50 dark:bg-primary-900/20 rounded-xl px-4 -mx-4">
                <span className="font-semibold text-dark-800 dark:text-white">{L.total}</span>
                <span className="font-bold text-xl text-primary-600 dark:text-primary-400">${selectedService.price + additionalPrice}</span>
              </div>
            </div>

            {/* Action Buttons */}
            <div className="flex gap-3 mt-6">
              <button
                onClick={() => setStep(1)}
                className="flex-1 flex items-center justify-center gap-2 px-4 py-3 rounded-xl font-medium text-gray-600 dark:text-gray-400 bg-gray-100 dark:bg-dark-700 hover:bg-gray-200 dark:hover:bg-dark-600 transition-colors"
              >
                <FaArrowLeft className="w-4 h-4" /> {L.back}
              </button>
              <button
                onClick={() => setStep(2)}
                disabled={!taxType}
                className="flex-1 flex items-center justify-center gap-2 px-4 py-3 rounded-xl font-medium text-white bg-gradient-to-r from-primary-500 to-primary-600 hover:from-primary-600 hover:to-primary-700 disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-200"
              >
                {L.continue} <FaArrowRight className="w-4 h-4" />
              </button>
            </div>
          </div>
        </div>
      )}

      {step === 2 && (
        <div className="max-w-2xl mx-auto">
          <div className="card p-6 space-y-6">
            <h3 className="text-lg font-semibold text-dark-800 dark:text-white mb-4">{loc.sectionTitle}</h3>
            
            <div className="space-y-4">
              {/* International Option */}
              <div
                className={`p-5 rounded-xl border-2 cursor-pointer transition-all duration-200 ${
                  location === 'international' 
                    ? 'border-primary-500 bg-primary-50 dark:bg-primary-900/20' 
                    : 'border-gray-200 dark:border-dark-600 hover:border-primary-300'
                }`}
                onClick={() => handleLocationSelect('international')}
              >
                <div className="flex items-center gap-4">
                  <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center ${
                    location === 'international' ? 'border-primary-500' : 'border-gray-300 dark:border-dark-500'
                  }`}>
                    {location === 'international' && <div className="w-2.5 h-2.5 rounded-full bg-primary-500" />}
                  </div>
                  <div className="flex items-center gap-3">
                    <FaGlobe className="w-6 h-6 text-primary-500" />
                    <div>
                      <h4 className="font-semibold text-dark-800 dark:text-white">{loc.internationalTitle}</h4>
                      <p className="text-sm text-gray-500 dark:text-gray-400">{loc.internationalSubtitle}</p>
                    </div>
                  </div>
                </div>
              </div>

              {/* India Option */}
              <div
                className={`p-5 rounded-xl border-2 cursor-pointer transition-all duration-200 ${
                  location === 'india' 
                    ? 'border-primary-500 bg-primary-50 dark:bg-primary-900/20' 
                    : 'border-gray-200 dark:border-dark-600 hover:border-primary-300'
                }`}
                onClick={() => handleLocationSelect('india')}
              >
                <div className="flex items-center gap-4">
                  <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center ${
                    location === 'india' ? 'border-primary-500' : 'border-gray-300 dark:border-dark-500'
                  }`}>
                    {location === 'india' && <div className="w-2.5 h-2.5 rounded-full bg-primary-500" />}
                  </div>
                  <div className="flex items-center gap-3">
                    <FaMapMarkerAlt className="w-6 h-6 text-primary-500" />
                    <div>
                      <h4 className="font-semibold text-dark-800 dark:text-white">{loc.indiaTitle}</h4>
                      <p className="text-sm text-gray-500 dark:text-gray-400">{loc.indiaRatePrefix}{exchangeRate.toFixed(2)}</p>
                    </div>
                  </div>
                </div>

                {location === 'india' && (
                  <div className="mt-4 ml-9" onClick={(e) => e.stopPropagation()}>
                    <label className="block text-sm font-medium text-dark-700 dark:text-gray-300 mb-2">{loc.selectStateLabel}</label>
                    <select
                      value={selectedFruit}
                      onChange={(e) => setSelectedFruit(e.target.value)}
                      className="w-full p-3 rounded-xl bg-white dark:bg-dark-700 border border-gray-200 dark:border-dark-600 text-dark-800 dark:text-white focus:outline-none focus:ring-2 focus:ring-primary-500/50 focus:border-primary-500"
                    >
                      <option value="">{loc.selectStatePlaceholder}</option>
                      {fruits.map((fruit) => (
                        <option key={fruit} value={fruit}>{fruit}</option>
                      ))}
                    </select>
                  </div>
                )}
              </div>
            </div>

            {/* Action Buttons */}
            <div className="flex gap-3 pt-4">
              <button 
                onClick={() => {
                  if (selectedService._id === 'service_1') {
                    setStep(1.1);
                  } else if (selectedService._id === 'service_2') {
                    setStep(1.5);
                  } else if (selectedService._id === 'service_3') {
                    setStep(1.25);
                  } else {
                    setStep(1);
                  }
                }}
                className="flex-1 flex items-center justify-center gap-2 px-6 py-3 rounded-xl font-medium text-gray-600 dark:text-gray-400 bg-gray-100 dark:bg-dark-700 hover:bg-gray-200 dark:hover:bg-dark-600 transition-colors"
              >
                <FaArrowLeft className="w-4 h-4" /> {L.back}
              </button>
              {location && (
                <button
                  onClick={handleProceedWithFruit}
                  disabled={location === 'india' && !selectedFruit}
                  className="flex-1 flex items-center justify-center gap-2 px-6 py-3 rounded-xl font-medium text-white bg-gradient-to-r from-primary-500 to-primary-600 hover:from-primary-600 hover:to-primary-700 disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-200"
                >
                  {L.proceed} <FaArrowRight className="w-4 h-4" />
                </button>
              )}
            </div>
          </div>
        </div>
      )}

      {step === 3 && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Order Summary */}
          <div className="card p-6">
            <h2 className="section-title mb-6">{co.orderDetailsTitle}</h2>
            <div className="space-y-3">
              <div className="flex justify-between py-2 border-b border-gray-100 dark:border-dark-700">
                <span className="text-gray-600 dark:text-gray-400">{co.serviceLabel}</span>
                <span className="font-medium text-dark-800 dark:text-white">{selectedService.name}</span>
              </div>

            {selectedService._id === 'service_1' && (
              <>
                <div className="flex justify-between py-2 border-b border-gray-100 dark:border-dark-700">
                  <span className="text-gray-600 dark:text-gray-400">{co.companyTypeLabel}</span>
                  <span className="font-medium text-dark-800 dark:text-white">{companyType}</span>
                </div>
                <div className="flex justify-between py-2 border-b border-gray-100 dark:border-dark-700">
                  <span className="text-gray-600 dark:text-gray-400">{co.stateLabel}</span>
                  <span className="font-medium text-dark-800 dark:text-white">{selectedState}</span>
                </div>
                <div className="flex justify-between py-2 border-b border-gray-100 dark:border-dark-700">
                  <span className="text-gray-600 dark:text-gray-400">{formationConfig.ourFeeLabel}</span>
                  <span className="font-medium text-dark-800 dark:text-white">
                    {promoCodeDetails && promoCodeDetails.applicableFee === 'Our Fee' && formationBreakdown ? (
                      <>
                        <span className="line-through text-gray-400 mr-2">{formatPriceForLocation(formationBreakdown.baseFee, { location, exchangeRate })}</span>
                        <span className="text-green-500">{formatPriceForLocation(formationBreakdown.baseFee - formationPromoDiscount, { location, exchangeRate })}</span>
                      </>
                    ) : (
                      formationBreakdown ? formatPriceForLocation(formationBreakdown.baseFee, { location, exchangeRate }) : '—'
                    )}
                  </span>
                </div>
                {formationPromoDiscount > 0 && (
                  <div className="flex justify-between py-2 border-b border-gray-100 dark:border-dark-700">
                    <span className="text-gray-600 dark:text-gray-400">{formationConfig.promoDiscountLabel} ({promoCodeDetails.code}):</span>
                    <span className="font-medium text-green-500">
                      -{formatPriceForLocation(formationPromoDiscount, { location, exchangeRate })}
                    </span>
                  </div>
                )}
                <div className="flex justify-between py-2 border-b border-gray-100 dark:border-dark-700">
                  <span className="text-gray-600 dark:text-gray-400">{formationConfig.stateFeeLabel}</span>
                  <span className="font-medium text-dark-800 dark:text-white">
                    {formationBreakdown ? formatPriceForLocation(formationBreakdown.stateFee, { location, exchangeRate }) : '—'}
                  </span>
                </div>
                <div className="flex justify-between py-2 border-b border-gray-100 dark:border-dark-700">
                  <span className="text-gray-600 dark:text-gray-400">{formationConfig.einServiceLabel}</span>
                  <span className="font-medium text-dark-800 dark:text-white">
                    {formationBreakdown ? formatPriceForLocation(formationBreakdown.einFee, { location, exchangeRate }) : '—'}
                  </span>
                </div>
                {!registeredAgent && formationBreakdown?.raFee > 0 && (
                  <div className="flex justify-between py-2 border-b border-gray-100 dark:border-dark-700">
                    <span className="text-gray-600 dark:text-gray-400">{formationConfig.registeredAgentLabel}</span>
                    <span className="font-medium text-dark-800 dark:text-white">{formatPriceForLocation(formationBreakdown.raFee, { location, exchangeRate })}</span>
                  </div>
                )}
              </>
            )}

            {selectedService._id === 'service_2' && taxType && (
              <div className="flex justify-between py-2 border-b border-gray-100 dark:border-dark-700">
                <span className="text-gray-600 dark:text-gray-400">{co.taxTypeLabel}</span>
                <span className="font-medium text-dark-800 dark:text-white">{taxTypeDetails[taxType].title}</span>
              </div>
            )}

            {selectedService._id === 'service_3' && (
              <div className="flex justify-between py-2 border-b border-gray-100 dark:border-dark-700">
                <span className="text-gray-600 dark:text-gray-400">{hs.hoursSelectedLabel}</span>
                <span className="font-medium text-dark-800 dark:text-white">{hours} hours</span>
              </div>
            )}

            <div className="flex justify-between py-2 border-b border-gray-100 dark:border-dark-700">
              <span className="text-gray-600 dark:text-gray-400">{co.billingFrequencyLabel}</span>
              <span className="font-medium text-dark-800 dark:text-white">{selectedService.frequency}</span>
            </div>
            
            {/* Promo Code Input */}
            <div className="py-4 border-b border-gray-100 dark:border-dark-700">
              <label className="block text-sm font-medium text-gray-600 dark:text-gray-400 mb-2 flex items-center gap-2">
                <FaTag className="text-primary-500" /> {co.havePromoCodeLabel}
              </label>
              <div className="flex gap-2">
                <div className="relative flex-1">
                  <input
                    type="text"
                    value={promoCode}
                    onChange={(e) => setPromoCode(e.target.value.toUpperCase())}
                    placeholder={co.enterCodePlaceholder}
                    className={`w-full p-3 rounded-xl bg-gray-50 dark:bg-dark-700 border ${promoCodeError ? 'border-red-500' : 'border-gray-200 dark:border-dark-600'} text-dark-800 dark:text-white focus:outline-none focus:ring-2 focus:ring-primary-500/50 focus:border-primary-500 uppercase`}
                    disabled={promoCodeDetails}
                  />
                  {promoCodeDetails && (
                    <div className="absolute right-3 top-1/2 -translate-y-1/2 text-green-500">
                      <FaCheck />
                    </div>
                  )}
                </div>
                {promoCodeDetails ? (
                  <button
                    onClick={clearPromoCode}
                    className="px-4 py-2 rounded-xl bg-red-100 dark:bg-red-900/20 text-red-600 dark:text-red-400 hover:bg-red-200 dark:hover:bg-red-900/40 transition-colors"
                  >
                    {L.clear}
                  </button>
                ) : (
                  <button
                    onClick={validatePromoCode}
                    disabled={promoCodeLoading || !promoCode}
                    className="px-6 py-2 rounded-xl bg-primary-500 text-white hover:bg-primary-600 disabled:opacity-50 transition-colors flex items-center gap-2"
                  >
                    {promoCodeLoading ? L.applying : L.apply}
                  </button>
                )}
              </div>
              {promoCodeError && (
                <p className="mt-2 text-red-500 text-sm flex items-center gap-1">
                  <FaExclamationCircle className="w-3 h-3" /> {promoCodeError}
                </p>
              )}
              {promoCodeDetails && (
                <p className="mt-2 text-green-500 text-sm flex items-center gap-1">
                  <FaCheck className="w-3 h-3" /> Applied: {promoCodeDetails.discountType === 'fixed' ? `$${promoCodeDetails.discountValue}` : `${promoCodeDetails.discountValue}%`} discount!
                </p>
              )}
            </div>

            {selectedService._id === 'service_2' && taxType && (
              <>
                <div className="flex justify-between py-2 border-b border-gray-100 dark:border-dark-700">
                  <span className="text-gray-600 dark:text-gray-400">{ts.basePriceLabel}</span>
                  <span className="font-medium text-dark-800 dark:text-white">{location === 'india' ? '₹' + (selectedService.price * exchangeRate).toFixed(2) : '$' + selectedService.price}</span>
                </div>
                {taxTypeDetails[taxType].questions.map((question) => (
                  taxQuestions[question.id] && (
                    <div key={question.id} className="flex justify-between py-2 border-b border-gray-100 dark:border-dark-700">
                      <span className="text-gray-600 dark:text-gray-400 text-sm">{question.text}:</span>
                      <span className="font-medium text-green-500">{location === 'india' ? '₹' + (question.price * exchangeRate).toFixed(2) : '+$' + question.price}</span>
                    </div>
                  )
                ))}
              </>
            )}

            {selectedService._id === 'service_3' && (
              <>
                <div className="flex justify-between py-2 border-b border-gray-100 dark:border-dark-700">
                  <span className="text-gray-600 dark:text-gray-400">{hs.hourlyRateLabel}</span>
                  <span className="font-medium text-dark-800 dark:text-white">{location === 'india' ? '₹' + (selectedService.price * exchangeRate).toFixed(2) : '$' + selectedService.price}</span>
                </div>
                <div className="flex justify-between py-2 border-b border-gray-100 dark:border-dark-700">
                  <span className="text-gray-600 dark:text-gray-400">{hs.hoursSelectedLabel}</span>
                  <span className="font-medium text-dark-800 dark:text-white">{hours}</span>
                </div>
              </>
            )}

            {location === 'india' && (
              <>
                <div className="flex justify-between py-2 border-b border-gray-100 dark:border-dark-700">
                  <span className="text-gray-600 dark:text-gray-400">
                    {co.gstLabel}
                    <button
                      onClick={() => setShowTaxBreakup(!showTaxBreakup)}
                      className="ml-2 text-primary-500 hover:text-primary-600"
                      title="Click to view tax breakdown"
                    >
                      ⓘ
                    </button>
                  </span>
                  <span className="font-medium text-dark-800 dark:text-white">₹{getConvertedPrice().gst}</span>
                </div>

                {showTaxBreakup && (
                  <div className="p-3 rounded-lg bg-gray-50 dark:bg-dark-700 text-sm text-gray-600 dark:text-gray-400">
                    {selectedFruit === 'Gujarat' ? co.gstCgstBreakup : co.gstIgstBreakup}
                  </div>
                )}

                <div className="flex justify-between py-2 border-b border-gray-100 dark:border-dark-700">
                  <span className="text-gray-600 dark:text-gray-400">{co.gstinLabel}</span>
                  <span className="font-medium text-dark-800 dark:text-white">
                    {gstin ? gstin : (
                      <button
                        onClick={() => setShowGstinModal(true)}
                        className="text-primary-500 hover:text-primary-600 text-sm font-medium"
                      >
                        {co.addGstinLabel}
                      </button>
                    )}
                  </span>
                </div>
              </>
            )}

            {/* Referral Credit Toggle */}
            {referralCredits > 0 && selectedService._id !== 'service_3' && (
              <div className="py-4 border-b border-gray-100 dark:border-dark-700">
                <label className="flex items-center justify-between cursor-pointer group">
                  <div className="flex items-center gap-3">
                    <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${applyReferralCredit ? 'bg-amber-100 dark:bg-amber-900/20 text-amber-600' : 'bg-gray-100 dark:bg-dark-700 text-gray-500'}`}>
                      <FaGift />
                    </div>
                    <div>
                      <p className="font-medium text-dark-800 dark:text-white">{co.useReferralCreditsLabel}</p>
                      <p className="text-xs text-gray-500 dark:text-gray-400">{formatTemplate(co.referralPointsHint, { points: referralCredits, value: (referralCredits / 1000 * 5).toFixed(2) })}</p>
                    </div>
                  </div>
                  <div 
                    onClick={() => setApplyReferralCredit(!applyReferralCredit)}
                    className={`w-12 h-6 rounded-full p-1 transition-colors duration-200 ${applyReferralCredit ? 'bg-primary-500' : 'bg-gray-300 dark:bg-dark-600'}`}
                  >
                    <div className={`w-4 h-4 rounded-full bg-white transition-transform duration-200 ${applyReferralCredit ? 'translate-x-6' : 'translate-x-0'}`} />
                  </div>
                </label>
              </div>
            )}

            {/* Price Details */}
            <div className="space-y-2 py-4">
              <div className="flex justify-between text-gray-600 dark:text-gray-400">
                <span>{L.subtotal}</span>
                <span>{location === 'india' ? `₹${getConvertedPrice().basePrice}` : `$${getConvertedPrice().basePrice}`}</span>
              </div>
              
              {parseFloat(getConvertedPrice().discount) > 0 && (
                <div className="flex justify-between text-green-500">
                  <span className="flex items-center gap-1"><FaTag className="w-3 h-3" /> {L.promoDiscount}</span>
                  <span>-{location === 'india' ? `₹${getConvertedPrice().discount}` : `$${getConvertedPrice().discount}`}</span>
                </div>
              )}

              {applyReferralCredit && parseFloat(getConvertedPrice().referralDiscount) > 0 && (
                <div className="flex justify-between text-amber-500">
                  <span className="flex items-center gap-1"><FaGift className="w-3 h-3" /> {L.referralDiscount}</span>
                  <span>-{location === 'india' ? `₹${getConvertedPrice().referralDiscount}` : `$${getConvertedPrice().referralDiscount}`}</span>
                </div>
              )}

              {location === 'india' && (
                <div className="flex justify-between text-gray-600 dark:text-gray-400">
                  <span>{co.gstLabel}</span>
                  <span>₹{getConvertedPrice().gst}</span>
                </div>
              )}
            </div>

            {/* Total */}
            <div className="flex justify-between py-4 mt-2 bg-primary-50 dark:bg-primary-900/20 rounded-xl px-4 -mx-4">
              <span className="font-bold text-dark-800 dark:text-white">{L.total.replace(':', '')}</span>
              <span className="font-bold text-xl text-primary-600 dark:text-primary-400">
                {location === 'india' ? `₹${getConvertedPrice().total}` : `$${getConvertedPrice().total}`}
              </span>
            </div>
            </div>
          </div>

          {/* Invoice Details Form */}
          <div className="card p-6">
            <h3 className="text-lg font-semibold text-dark-800 dark:text-white mb-6">{co.invoiceDetailsTitle}</h3>

            <div className="space-y-4">
              <div>
                <input
                  type="text"
                  placeholder={co.fullNamePlaceholder}
                  value={fullName}
                  onChange={(e) => {
                    setFullName(e.target.value);
                    if (e.target.value) {
                      setFormErrors(prev => ({ ...prev, fullName: '' }));
                    }
                  }}
                  className={`w-full p-4 rounded-xl bg-gray-50 dark:bg-dark-700 border ${formErrors.fullName ? 'border-red-500' : 'border-gray-200 dark:border-dark-600'} text-dark-800 dark:text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-primary-500/50 focus:border-primary-500 transition-all duration-200`}
                  required
                />
                {formErrors.fullName && (
                  <div className="mt-2 flex items-center gap-2 text-red-500 text-sm">
                    <FaExclamationCircle /> {formErrors.fullName}
                  </div>
                )}
              </div>

              <div>
                <input
                  type="text"
                  placeholder={co.fullAddressPlaceholder}
                  value={address}
                  onChange={(e) => {
                    setAddress(e.target.value);
                    if (e.target.value) {
                      setFormErrors(prev => ({ ...prev, address: '' }));
                    }
                  }}
                  className={`w-full p-4 rounded-xl bg-gray-50 dark:bg-dark-700 border ${formErrors.address ? 'border-red-500' : 'border-gray-200 dark:border-dark-600'} text-dark-800 dark:text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-primary-500/50 focus:border-primary-500 transition-all duration-200`}
                  required
                />
                {formErrors.address && (
                  <div className="mt-2 flex items-center gap-2 text-red-500 text-sm">
                    <FaExclamationCircle /> {formErrors.address}
                  </div>
                )}
              </div>

              {location === 'india' && selectedFruit && (
                <div className="p-4 rounded-xl bg-gray-50 dark:bg-dark-700 border border-gray-200 dark:border-dark-600">
                  <p className="text-dark-800 dark:text-white"><span className="font-medium">{co.invoiceStateLabel}</span> {selectedFruit}</p>
                </div>
              )}

              {location === 'india' && (
                <div className="p-4 rounded-xl bg-gray-50 dark:bg-dark-700 border border-gray-200 dark:border-dark-600">
                  <p className="text-dark-800 dark:text-white"><span className="font-medium">{co.countryIndiaLabel}</span> {co.countryIndiaValue}</p>
                </div>
              )}

              {location === 'international' && (
                <div>
                  <label className="block text-sm font-medium text-dark-700 dark:text-gray-300 mb-2">{co.selectCountryLabel}</label>
                  <select
                    value={country}
                    onChange={(e) => {
                      setCountry(e.target.value);
                      if (e.target.value) {
                        setFormErrors(prev => ({ ...prev, country: '' }));
                      }
                    }}
                    className={`w-full p-4 rounded-xl bg-gray-50 dark:bg-dark-700 border ${formErrors.country ? 'border-red-500' : 'border-gray-200 dark:border-dark-600'} text-dark-800 dark:text-white focus:outline-none focus:ring-2 focus:ring-primary-500/50 focus:border-primary-500 transition-all duration-200`}
                    required
                  >
                    <option value="">{co.selectCountryPlaceholder}</option>
                    {allCountries.map((countryName) => (
                      <option key={countryName} value={countryName}>
                        {countryName}
                      </option>
                    ))}
                  </select>
                  {formErrors.country && (
                    <div className="mt-2 flex items-center gap-2 text-red-500 text-sm">
                      <FaExclamationCircle /> {formErrors.country}
                    </div>
                  )}
                </div>
              )}
            </div>

            {/* Action Buttons */}
            <div className="flex gap-3 mt-6">
              <button 
                onClick={() => setStep(2)}
                className="flex-1 flex items-center justify-center gap-2 px-6 py-3 rounded-xl font-medium text-gray-600 dark:text-gray-400 bg-gray-100 dark:bg-dark-700 hover:bg-gray-200 dark:hover:bg-dark-600 transition-colors"
              >
                <FaArrowLeft className="w-4 h-4" /> {L.back}
              </button>
              <RazorpayPayment
                service={selectedService}
                user={user}
                location={location}
                country={country}
                fullName={fullName}
                address={address}
                exchangeRate={exchangeRate}
                gstin={gstin}
                selectedFruit={selectedFruit}
                taxType={taxType}
                taxQuestions={taxQuestions}
                additionalPrice={additionalPrice}
                hours={selectedService?._id === 'service_3' ? hours : undefined}
                companyType={selectedService?._id === 'service_1' ? companyType : undefined}
                selectedState={selectedService?._id === 'service_1' ? selectedState : undefined}
                hasSSN={selectedService?._id === 'service_1' ? hasSSN : undefined}
                registeredAgent={selectedService?._id === 'service_1' ? registeredAgent : undefined}
                promoCodeDetails={promoCodeDetails}
                formationConfig={formationConfig}
                stateFees={stateFees}
                applyReferralCredit={applyReferralCredit}
                referralCredits={referralCredits}
                onValidationError={(errors) => {
                  setFormErrors(errors);
                  toast.error('Please fill in all required fields');
                }}
                renderButton={(handlePayment) => (
                  <button
                    className="flex-1 flex items-center justify-center gap-2 px-6 py-3 rounded-xl font-medium text-white bg-gradient-to-r from-primary-500 to-primary-600 hover:from-primary-600 hover:to-primary-700 transition-all duration-200"
                    onClick={() => {
                      setFormErrors({});
                      const errors = {};
                      if (!fullName.trim()) errors.fullName = 'Full Name is required';
                      if (!address.trim()) errors.address = 'Address is required';
                      if (location === 'international' && !country) errors.country = 'Country is required';
                      if (Object.keys(errors).length > 0) {
                        setFormErrors(errors);
                        toast.error('Please fill in all required fields');
                        return;
                      }
                      handlePayment();
                    }}
                  >
                    {co.proceedToPayPrefix} {location === 'india' ? 
                      `₹${getConvertedPrice().total}` : 
                      `$${parseFloat(getConvertedPrice().total).toFixed(2)}`
                    }
                  </button>
                )}
              />
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Pricing;